import { HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { AppState } from '@app/reducers';
import { AuthenticationModuleApiSuffixModels, LoggedInUserModel, ModulesBasedApiSuffix, RoutingStates, SharedTableFilteredDataRemoved, decryptData, encryptData } from '@app/shared';
import { JwtHelperService } from "@auth0/angular-jwt";
import { MsalService } from '@azure/msal-angular';
import { encodedTokenSelector, encryptedUserIdSelector, loggedInUserData } from '@modules/others';
import { Store, select } from '@ngrx/store';
import { of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Logout } from '../../modules/others/auth.actions';
import { DynamicEagerLoadingRemove, StaticEagerLoadingRemove } from '../../modules/others/eager-loading.actions';
import { LeadCreationStepperParamsRemoveAction, LeadCreationUserDataRemoveAction, LeadHeaderDataRemoveAction } from '../../modules/sales/components/task-management/lead-creation-ngrx-files/lead-creation.actions';
import { AgentLogoutDataRemoved } from '../../shared/layouts/app-header/state/agent-login.actions';
import { PageBasedPermissionsRemoved, SidebarDataRemoved } from '../../shared/layouts/layout.actions';
import { CrudService } from './crud.service';
import { RxjsService } from './rxjs.services';
import { SignalrConnectionService } from './signalr-connection.service';

const encodedJWTTokenKey = 'encodedJWTToken';
const encryptedUserIdTokenKey = 'encryptedUserIdTokenKey';
const loggedUserDataKey = 'loggedUserData';
const agentLoginDataKey = 'agentLoginData';
const agentLoginMediaTypesKey = 'agentLoginMediaTypes';
const agentStatusKey = 'agentStatus';
const deviceIdKey = 'deviceIdKey';
const sidebarMenuKey = 'sidebarMenuKey';
const defaultRedirectRouteUrlKey = 'defaultRedirectRouteUrl';
const refreshTokenKey = 'refreshToken';
const currentComponentPageBasedPermissionsKey = "";
const moduleBasedComponentPermissionsLey = "";
const tableFilteredFieldsKey = 'tableFilteredFields';
const openScapePabxIdKey = 'openScapePabxIdKey';
const agentKey = 'agentKey';
const agentEncryptedPasswordKey = "agentEncryptedPasswordKey";
const userDetailsKey = 'userDetails';
const bocServices = 'bocServicesDetails';
/**
 * Provides storage for authentication jwt token.
 */
@Injectable({ providedIn: 'root' })
export class UserService {
  private token: string | null = null;
  loggedInUserDataModel: LoggedInUserModel;
  openedPrimeDialog: any = [];
  encryptedUserIdToken = "";
  encodedToken = "";
  encryptedEncodedToken = "";

  constructor(private store: Store<AppState>, private router: Router, private dialog: MatDialog, private rxjsService: RxjsService,
    private signalrConnectionService: SignalrConnectionService, private msalService: MsalService, private crudService: CrudService) {
    this.store.pipe(select(encryptedUserIdSelector)).subscribe((encryptedUserId: string) => {
      this.encryptedUserIdToken = encryptedUserId;
    });
    this.store.pipe(select(encodedTokenSelector)).subscribe((encodedToken: string) => {
      this.encodedToken = encodedToken;
    });
    this.store.pipe(select(loggedInUserData)).subscribe((loggedInUserData: LoggedInUserModel) => {
      this.loggedInUserDataModel = new LoggedInUserModel(loggedInUserData);
    });
    this.rxjsService.getAnyPropertyValue().subscribe((encryptedEncodedToken) => {
      if (encryptedEncodedToken) {
        this.encryptedEncodedToken = encryptedEncodedToken;
      }
    });
  }

  /**
   * Checks if the token is validated.
   * @return True  if the token is validated.
   */
  isTokenValid(): boolean {
    return this.token !== null ? true : false;
  }

  /**
    * Gets logged in decoded user data.
    * @param encodedToken which is extracted from the object when the user logs in.
    */
  getDecodedLoggedInData(encodedToken: string): object | null {
    const helper = new JwtHelperService();
    const isExpired = helper.isTokenExpired(encodedToken);
    if (!isExpired) {
      return helper.decodeToken(encodedToken);
    }
    return null;
  }

  /**
  * Sets logged in user data.
  * @param userData which is in object where the object is sent from the server whenever the user logged In.
  */
  set loggedInUserData(userData: LoggedInUserModel | null) {
    if (userData) {
      encryptData(userData, loggedUserDataKey);
    }
    else {
      localStorage.removeItem(loggedUserDataKey);
    }
  }

  /**
   * Gets the user data.
   * @return The object which contains user data.
   */
  get loggedInUserData(): LoggedInUserModel | null {
    if (localStorage.getItem(loggedUserDataKey)) {
      return decryptData(loggedUserDataKey);
    }
  }

  /**
  * Sets logged in user data.
  * @param encodedToken which is the encoded token given from server response when user logs in.
  */
  set encodedJWTToken(encodedToken: string | null) {
    if (encodedToken) {
      encryptData(encodedToken, encodedJWTTokenKey);
    }
    else {
      localStorage.removeItem(encodedJWTTokenKey);
    }
  }

  get encodedJWTToken() {
    if (localStorage.getItem(encodedJWTTokenKey)) {
      return decryptData(encodedJWTTokenKey);
    }
  }

  /**
  * Sets encryptedUserId.
  * @param encryptedUserId which is the decrypted token from the server's encodedToken when the user logs in.
  */
  set encryptedUserId(encryptedUserId: string | null) {
    if (encryptedUserId) {
      encryptData(encryptedUserId, encryptedUserIdTokenKey);
    }
    else {
      localStorage.removeItem(encryptedUserIdTokenKey);
    }
  }

  get encryptedUserId() {
    if (localStorage.getItem(encryptedUserIdTokenKey)) {
      return decryptData(encryptedUserIdTokenKey);
    }
  }

  set sidebarMenuData(sidebarMenuList: Array<object> | null) {
    if (sidebarMenuList) {
      encryptData(sidebarMenuList, sidebarMenuKey);
    } else {
      localStorage.removeItem(sidebarMenuKey);
    }
  }

  get sidebarMenuData() {
    if (localStorage.getItem(sidebarMenuKey)) {
      return decryptData(sidebarMenuKey);
    }
  }

  set moduleBasedComponentPermissions(moduleBasedComponentPermissions: Array<object> | null) {
    if (moduleBasedComponentPermissions) {
      encryptData(moduleBasedComponentPermissions, moduleBasedComponentPermissionsLey);
    } else {
      localStorage.removeItem(moduleBasedComponentPermissionsLey);
    }
  }

  get moduleBasedComponentPermissions() {
    if (localStorage.getItem(moduleBasedComponentPermissionsLey)) {
      return decryptData(moduleBasedComponentPermissionsLey);
    }
  }

  set currentComponentPageBasedPermissions(currentComponentPageBasedPermissions: Array<object> | null) {
    if (currentComponentPageBasedPermissions) {
      encryptData(currentComponentPageBasedPermissions, currentComponentPageBasedPermissionsKey);
    } else {
      localStorage.removeItem(currentComponentPageBasedPermissionsKey);
    }
  }

  get currentComponentPageBasedPermissions() {
    if (localStorage.getItem(currentComponentPageBasedPermissionsKey)) {
      return decryptData(currentComponentPageBasedPermissionsKey);
    }
  }

  set agentLoginData(agentExtensionNo: string | null) {
    if (agentExtensionNo) {
      encryptData(agentExtensionNo, agentLoginDataKey);
    } else {
      localStorage.removeItem(agentLoginDataKey);
    }
  }

  get agentLoginData() {
    if (localStorage.getItem(agentLoginDataKey)) {
      return decryptData(agentLoginDataKey);
    }
  }

  get agentLoginMediaTypes() {
    if (localStorage.getItem(agentLoginMediaTypesKey)) {
      return decryptData(agentLoginMediaTypesKey);
    }
  }

  set agentLoginMediaTypes(mediaTypes: string[]) {
    if (mediaTypes && mediaTypes.length > 0) {
      encryptData(mediaTypes, agentLoginMediaTypesKey);
    } else {
      localStorage.removeItem(agentLoginMediaTypesKey);
    }
  }

  get agentStatus() {
    if (localStorage.getItem(agentStatusKey)) {
      return decryptData(agentStatusKey);
    }
  }

  set agentStatus(agentStatus: RoutingStates) {
    if (agentStatus) {
      encryptData(agentStatus, agentStatusKey);
    } else {
      localStorage.removeItem(agentStatusKey);
    }
  }

  get deviceId() {
    if (localStorage.getItem(deviceIdKey)) {
      return decryptData(deviceIdKey);
    }
  }

  set deviceId(deviceId: string) {
    if (deviceId) {
      encryptData(deviceId, deviceIdKey);
    } else {
      localStorage.removeItem(deviceIdKey);
    }
  }

  get agentKey() {
    if (localStorage.getItem(agentKey)) {
      return decryptData(agentKey);
    }
  }

  set agentKey(agentKeyValue: string | null) {
    if (agentKeyValue) {
      encryptData(agentKeyValue, agentKey);
    } else {
      localStorage.removeItem(agentKey);
    }
  }

  get agentEncryptedPassword() {
    if (localStorage.getItem(agentEncryptedPasswordKey)) {
      return decryptData(agentKey);
    }
  }

  set agentEncryptedPassword(agentPassword: string | null) {
    if (agentPassword) {
      encryptData(agentPassword, agentEncryptedPasswordKey);
    } else {
      localStorage.removeItem(agentEncryptedPasswordKey);
    }
  }

  set defaultRedirectRouteUrl(routeUrl: string | null) {
    if (routeUrl) {
      encryptData(routeUrl, defaultRedirectRouteUrlKey);
    }
    else {
      localStorage.removeItem(defaultRedirectRouteUrlKey);
    }
  }

  get defaultRedirectRouteUrl() {
    if (localStorage.getItem(defaultRedirectRouteUrlKey)) {
      return decryptData(defaultRedirectRouteUrlKey);
    }
  }

  get refreshToken() {
    if (localStorage.getItem(refreshTokenKey)) {
      return decryptData(refreshTokenKey);
    }
  }

  set refreshToken(refreshToken: string | null) {
    if (refreshToken) {
      encryptData(refreshToken, refreshTokenKey);
    }
    else {
      localStorage.removeItem(refreshTokenKey);
    }
  }

  get chatUserDetails() {
    if (localStorage.getItem(userDetailsKey)) {
      return decryptData(userDetailsKey);
    }
  }

  set chatUserDetails(userDetails: object | any | null) {
    if (userDetails) {
      encryptData(userDetails, userDetailsKey);
    }
    else {
      localStorage.removeItem(userDetailsKey);
    }
  }

  get bocServiceDetails() {
    if (localStorage.getItem(bocServices)) {
      return decryptData(bocServices);
    }
  }

  set bocServiceDetails(userDetails: object | any | null) {
    if (userDetails) {
      encryptData(userDetails, bocServices);
    }
    else {
      localStorage.removeItem(bocServices);
    }
  }

  setVersionTokenHeader(request: HttpRequest<any>): HttpRequest<any> {
    let headers = new HttpHeaders({
      VersioningAuthorizationKey: environment['versionApiKey'],
    });
    request = request.clone({
      headers
    });
    return request;
  }

  setHeaderWithoutToken(request: HttpRequest<any>): HttpRequest<any> {
    let headers = new HttpHeaders({
      RequestedFrom: "CRM",
    });
    request = request.clone({
      headers
    });
    return request;
  }

  setTokenHeader(request: HttpRequest<any>, encodedToken?: string): HttpRequest<any> {
    let headers: HttpHeaders;
    if (request.url.includes('v2/users/ad/login')) {
      headers = new HttpHeaders({
        Authorization: `Bearer ${this.encryptedEncodedToken}`
      });
    }
    else if (request.url.includes('v2/user/logout')) {
      headers = new HttpHeaders({
        Authorization: `Bearer ${this.encodedToken}`,
      });
    }
    else {
      headers = new HttpHeaders({
        Authorization: `Bearer ${encodedToken ? encodedToken : this.encodedToken}`,
        RequestedFrom: "CRM",
        LoggedinUserId: this.encryptedUserIdToken
      });
    }
    request = request.clone({ headers });
    return request;
  }

  onLogout(returnType = 'void') {
    // If the user is internal we have adReferenceId then need to go into the azure AD logout process
    if (this.loggedInUserDataModel.adReferenceId) {
      sessionStorage.setItem('userId', this.loggedInUserDataModel.userId);
      setTimeout(() => {
        this.msalService?.logout();
        this.removeAllStorageDataAndAppData('from AD logout');
      });
    }
    else {
      if (returnType == 'void') {
        this.onAppLogout();
      }
      else if (returnType == 'observable') {
        return this.crudService.create(ModulesBasedApiSuffix.AUTHENTICATION, AuthenticationModuleApiSuffixModels.LOGOUT_V2, {
          userId: this.loggedInUserData.userId,
          userDeviceId: localStorage.getItem('dId')
        });
      }
    }
  }

  logoutV2Subscription: Subscription;
  onAppLogout(userId?: string) {
    if (!this.loggedInUserData?.userId && !userId) return;
    this.logoutV2Subscription = this.crudService.create(ModulesBasedApiSuffix.AUTHENTICATION, AuthenticationModuleApiSuffixModels.LOGOUT_V2, {
      userId: userId ? userId : this.loggedInUserData.userId,
      userDeviceId: localStorage.getItem('dId')
    }).pipe(map(result => result), catchError(error => of(error)))
      .subscribe((response) => {
        if (response?.isSuccess && response?.statusCode == 200) {
          this.removeAllStorageDataAndAppData();
          this.rxjsService.setLogoutResponse(true);
          this.logoutV2Subscription.unsubscribe();
        }
      });
  }

  removeAllStorageDataAndAppData(pageType?: any) {
    this.store.dispatch(new Logout());
    if (pageType === 'from AD logout') {
      setTimeout(() => {
        this.store.dispatch(new SidebarDataRemoved());
      }, 1000);
    }
    this.store.dispatch(new AgentLogoutDataRemoved());
    this.store.dispatch(new StaticEagerLoadingRemove());
    this.store.dispatch(new DynamicEagerLoadingRemove());
    this.store.dispatch(new LeadCreationUserDataRemoveAction());
    this.store.dispatch(new LeadCreationStepperParamsRemoveAction());
    this.store.dispatch(new LeadHeaderDataRemoveAction());
    this.store.dispatch(new PageBasedPermissionsRemoved());
    this.store.dispatch(new SharedTableFilteredDataRemoved());
    this.rxjsService.setUniqueCallId(null);
    this.rxjsService.setCustomerContactNumber(null);
    this.rxjsService.setscheduleCallbackDetails(null);
    this.rxjsService.setAnyPropertyValue(null);
    this.signalrConnectionService.closeAllHubConnections();
    localStorage.clear();
    if (pageType !== 'from AD logout') {
      sessionStorage.clear();
    }
    // close all opened angular material popups thoughout the entire application
    this.dialog.closeAll();
    this.closeAllDynamicDialogs();
    if (pageType !== 'from AD logout') {
      this.router.navigateByUrl('login');
    }
  }

  closeAllDynamicDialogs() {
    this.openedPrimeDialog?.forEach(el => {
      if (el) {
        el?.close();
      }
    });
  }

  /**
  * Sets the table's filtered fields.
  * @param tableFilteredFields which is in object where the user filtered fields in the desired table.
  */
  set tableFilteredFields(tableFilteredFields) {
    if (tableFilteredFields) {
      encryptData(tableFilteredFields, tableFilteredFieldsKey);
    }
    else {
      localStorage.removeItem(tableFilteredFieldsKey);
    }
  }

  /**
   * Gets table's filtered fields.
   * @return the user filtered fields in the desired table.
   */
  get tableFilteredFields() {
    if (localStorage.getItem(tableFilteredFieldsKey)) {
      return decryptData(tableFilteredFieldsKey);
    }
  }

  get openScapePabxId() {
    if (localStorage.getItem(openScapePabxIdKey)) {
      return decryptData(openScapePabxIdKey);
    }
  }

  set openScapePabxId(openScapePabxId: string) {
    if (openScapePabxId) {
      encryptData(openScapePabxId, openScapePabxIdKey);
    }
    else {
      localStorage.removeItem(openScapePabxIdKey);
    }
  }
}
